import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import apiClient from '@/api/client';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

// ─── Offene Fragen ───────────────────────────────────────────────────────────
//
// Was im Stammbaum fehlt, gesammelt und nach Aussicht auf Erfolg geordnet.
//
// Die Seite war zuvor als Ermittlungsspiel gebaut: Akten, Erfahrungspunkte,
// „Die Geisterhochzeit", ein Detektiv-Symbol in der Überschrift. Das las sich
// munter, traf aber den Gegenstand nicht – hier geht es um verstorbene
// Angehörige und um Arbeit, die Wochen in Archiven bedeuten kann. Wer den
// Sterbeeintrag seiner Großmutter sucht, löst keinen Fall und sammelt keine
// Punkte.
//
// Geblieben ist die Sache dahinter, und sie ist mehr wert als die Verkleidung:
// eine Liste, was fehlt, wie schwer es zu finden sein dürfte und wo man sucht.

type Aufwand = 'gering' | 'mittel' | 'hoch' | 'sehr hoch';

interface OffeneFrage {
  id: string;
  nummer: string;
  titel: string;
  beschreibung: string;
  wegweiser: string;
  personId: string | null;
  personName: string | null;
  feld: string;
  aufwand: Aufwand;
}

interface OffeneFragenAntwort {
  fragen: OffeneFrage[];
  /** Wie viele es insgesamt gibt – die Antwort ist gedeckelt. */
  gesamt: number;
  /** Die wahren Summen je Stufe. Die Filter zählten früher nur, was ankam. */
  nachAufwand: Record<Aufwand, number>;
}

async function ladeOffeneFragen(): Promise<OffeneFragenAntwort> {
  const { data } = await apiClient.get<OffeneFragenAntwort>('/cold-cases');
  return data;
}

const FELD_LABELS: Record<string, string> = {
  birthDate: 'Geburtsdatum',
  birthPlace: 'Geburtsort',
  deathDate: 'Sterbedatum',
  parents: 'Eltern',
  marriageDate: 'Datum der Eheschließung',
};

// Zurückhaltende Farben: der Aufwand ist eine Einschätzung, keine Warnung.
// Rot für „sehr hoch" hieße, etwas stimme nicht – dabei ist eine schwer zu
// beantwortende Frage völlig in Ordnung.
const AUFWAND: Record<Aufwand, { label: string; cls: string }> = {
  gering: { label: 'Geringer Aufwand', cls: 'bg-slate-100 text-slate-600' },
  mittel: { label: 'Mittlerer Aufwand', cls: 'bg-slate-100 text-slate-600' },
  hoch: { label: 'Hoher Aufwand', cls: 'bg-slate-100 text-slate-500' },
  'sehr hoch': { label: 'Sehr hoher Aufwand', cls: 'bg-slate-100 text-slate-500' },
};

const FILTER: Array<{ label: string; wert: Aufwand | 'alle' }> = [
  { label: 'Alle', wert: 'alle' },
  { label: 'Geringer Aufwand', wert: 'gering' },
  { label: 'Mittlerer', wert: 'mittel' },
  { label: 'Hoher', wert: 'hoch' },
  { label: 'Sehr hoher', wert: 'sehr hoch' },
];

/** `**fett**` im Text der Beschreibung. */
function MitFett({ text }: { text: string }) {
  return (
    <>
      {text.split('**').map((teil, i) =>
        i % 2 === 1 ? <strong key={i} className="font-medium text-slate-800">{teil}</strong> : <span key={i}>{teil}</span>,
      )}
    </>
  );
}

function FrageKarte({ frage }: { frage: OffeneFrage }) {
  const aufwand = AUFWAND[frage.aufwand];
  const feld = FELD_LABELS[frage.feld] ?? frage.feld;

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-medium text-slate-900">{frage.titel}</h3>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] ${aufwand.cls}`}>
          {aufwand.label}
        </span>
      </div>

      <p className="text-sm leading-relaxed text-slate-600">
        <MitFett text={frage.beschreibung} />
      </p>

      {/* Der Wegweiser ist der eigentliche Nutzen der Seite: nicht DASS etwas
          fehlt – das sieht man auch am leeren Feld –, sondern wo man sucht. */}
      <p className="rounded-md bg-slate-50 px-2.5 py-2 text-xs leading-relaxed text-slate-500">
        {frage.wegweiser}
      </p>

      <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-1">
        <span className="text-xs text-slate-400">Fehlt: {feld}</span>
        {frage.personId && (
          <Link
            to={`/?person=${frage.personId}`}
            className="text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            {frage.personName ?? 'Zur Person'} öffnen
          </Link>
        )}
      </div>
    </div>
  );
}

export default function ColdCasesPage() {
  const [filter, setFilter] = useState<Aufwand | 'alle'>('alle');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['offene-fragen'],
    queryFn: ladeOffeneFragen,
  });

  const fragen = data?.fragen;
  const gefiltert =
    filter === 'alle' ? (fragen ?? []) : (fragen ?? []).filter((f) => f.aufwand === filter);

  // Die WAHREN Summen, nicht die der angekommenen Liste. Vorher wurde gezählt,
  // was da war – und sobald die Antwort gedeckelt war, log der Knopf.
  const anzahl = (a: Aufwand) => data?.nachAufwand[a] ?? 0;
  const gekuerzt = data ? data.gesamt > data.fragen.length : false;

  return (
    <AppLayout>
      <div className="mx-auto max-w-5xl px-4 py-6 sm:py-8">
        <header className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Offene Fragen</h1>
          <p className="mt-1 text-sm text-slate-500">
            Was in Ihrem Stammbaum noch fehlt – mit einem Hinweis, wo sich die Antwort
            üblicherweise findet. Das Erreichbare steht oben.
          </p>
        </header>

        <div className="mb-6 flex flex-wrap gap-2">
          {FILTER.map((opt) => (
            <button
              key={opt.wert}
              onClick={() => setFilter(opt.wert)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                filter === opt.wert
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {opt.label}
              {data && (
                <span className="ml-1.5 text-[11px] opacity-70">
                  ({opt.wert === 'alle' ? data.gesamt : anzahl(opt.wert)})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Nur sagen, wenn es etwas zu sagen gibt. Eine Liste, die alles zeigt,
            braucht keinen Hinweis darauf, dass sie es tut. */}
        {gekuerzt && data && (
          <p className="mb-4 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
            Angezeigt werden {data.fragen.length} von {data.gesamt} Fragen – aus jeder
            Aufwandsstufe ein Teil. Wer eine Stufe abarbeitet, sieht die nächsten beim
            erneuten Laden.
          </p>
        )}

        {isLoading && (
          <div className="flex justify-center py-20">
            <LoadingSpinner />
          </div>
        )}

        {isError && (
          <p className="py-20 text-center text-sm text-red-500">
            Die offenen Fragen konnten nicht geladen werden.
          </p>
        )}

        {fragen && gefiltert.length === 0 && (
          <p className="rounded-lg border border-dashed border-slate-200 px-4 py-16 text-center text-sm text-slate-400">
            {fragen.length === 0
              ? 'Nichts offen – in diesem Stammbaum ist alles erfasst, was diese Seite prüft.'
              : 'Unter diesem Filter steht nichts.'}
          </p>
        )}

        {gefiltert.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {gefiltert.map((f) => (
              <FrageKarte key={f.id} frage={f} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
